import { FC, useRef } from "react";
import { Box, Skeleton, Stack } from "@mui/material";
import { useTranslation } from "react-i18next";
import GraphMap from "../../../../../components/charts/GraphMap";
import HeatLayer from "../../../../../components/charts/GraphMap/layers/HeatLayer";
import ScaleLegend from "../../../../../components/charts/GraphMap/legends/ScaleLegend";
import DropdownSelect from "../../../../../components/common/DropdownSelect";
import PaperCard from "../../../../../components/widgets/PaperCard";
import PaperCardHeader from "../../../../../components/widgets/PaperCard/PaperCardHeader";
import { CountriesEmissionsResultsDTO } from "../../../../../datasources/api-client/data-contracts";

interface Props {
	color: string;
	title: string;
	data: CountriesEmissionsResultsDTO | undefined;
	isLoading: boolean;
	onReload: VoidFunction;
	selectedSource: string;
	onSourceChange: (value: string) => void;
}

const EmissionsMap: FC<Props> = ({ color, title, data, isLoading, onReload, selectedSource, onSourceChange }) => {
	const containerRef = useRef<HTMLDivElement | null>(null);

	const { t } = useTranslation("result.transport");

	// Create a menu with object for this select beacuse we need different type for the menu value
	const menuOptions = [
		{ key: "trips-origin", label: t("starting country") },
		{ key: "trips-destination", label: t("destination country") },
	];

	const map = (
		<GraphMap tileProvider="draw" isLoading={isLoading} zoom={3} maxZoom={20} minZoom={2}>
			<HeatLayer color={color} data={data?.data} scale={data?.scale} />
		</GraphMap>
	);

	if (isLoading) {
		return <Skeleton variant="rounded" height="100%" width="100%" />;
	}

	return (
		<PaperCard
			ref={containerRef}
			header={
				<PaperCardHeader
					containerRef={containerRef}
					title={title}
					element={
						<DropdownSelect
							dropdownItems={menuOptions}
							value={selectedSource}
							onChange={onSourceChange}
							variant="outlined"
						/>
					}
					isLoading={isLoading}
					onReload={onReload}
				/>
			}
			content={
				<Stack direction="column" height="100%" width="100%" textAlign="center" position={"relative"}>
					{map}
					<Box pt={1}>
						<ScaleLegend title="tC02" scale={data?.scale} baseColor={color} />
					</Box>
				</Stack>
			}
			sxContent={{ paddingRight: 0, paddingLeft: 0 }}
		/>
	);
};

export default EmissionsMap;
